<template>
  <article>
    <activity-stage
      :customRootContainerClass="'q-and-a'"
      :customContentContainerStyles="{ height: '100%' }"
    >
      <article slot="content">
        <div v-if="showActivity" style="display: flex">
          <div class="left-side">
            <img
              :alt="currentRound?.contentImage?.image.description"
              :src="currentRound?.contentImage?.image.src ?? ''"
            />
          </div>
          <div class="right-side">
            <div class="q-a-instructions">
              <audio-toggle-button :sync-on-render="true"></audio-toggle-button>
              <span v-text="customInstructions"></span>
            </div>
            <div class="steps-container">
              <div class="step-wrapper step-question">
                <span class="label">Question:</span>
                <blinking-cursor-text
                  :text="currentRound.steps[0].question"
                  :text-step="0"
                  :round-id="currentRound.roundIndex"
                >
                </blinking-cursor-text>
              </div>
              <div aria-live="assertive" class="step-wrapper step-answer" role="region">
                <span class="label">Answer:</span>
                <blinking-cursor-text
                  :text="currentRound.steps[0].answer"
                  :text-step="1"
                  :round-id="currentRound.roundIndex"
                >
                </blinking-cursor-text>
              </div>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import AccessibilityHelper from 'mixins/AccessibilityHelper';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import AudioToggleButton from 'components/buttons/AudioToggleButton';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper, AccessibilityHelper],

  components: {
    ActivityStage,
    AudioToggleButton,
    BlinkingCursorText
  },

  data() {
    return {
      customInstructions: 'Type the question and answer.',
      showActivity: false,
      completed: 0,
      instructionsFilename: undefined
    };
  },

  methods: {
    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const container = document.querySelector('.q-and-a');
      container.style.height = `calc(100% - ${keyboard.offsetHeight + 20}px)`;
    },

    async loadActivity() {
      const activityState = this.getActivityState();
      await this.loadImages(activityState.rounds.map(({ contentImage }) => contentImage.image.src));
      this.createRounds(activityState);
    },

    async loadImages(images) {
      this.queueImage(images);
      this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds: rounds.map((round, roundIndex) => ({
          ...round,
          roundIndex
        }))
      };
    },

    fadeInContainer() {
      TweenMax.to('.q-and-a', 1, {
        opacity: 1,
        onComplete() {
          TweenMax.to('.step-question', 1, { opacity: 1 });
        }
      });
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.showActivity = true;
      this.overwriteProgressText({
        currentText: this.currentRound.steps[0].question
      });
      this.$nextTick(() => {
        this.setContainerHeight();
        this.$nextTick(this.fadeInContainer);
      });
    },

    handleNewRound() {
      const self = this;
      self.completed = 0;

      TweenMax.to('.q-and-a', 0.5, {
        opacity: 0,
        onComplete() {
          // hide question and answer from previous round
          TweenMax.to('.step-wrapper', 0, { opacity: 0 });

          self.setupNewRound();
          self.overwriteProgressText({
            currentText: self.currentRound.steps[0].question
          });
          self.$nextTick(() => self.fadeInContainer());
        }
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();
        this.completed += 1;

        if (this.completed === 1) {
          this.overwriteProgressText({
            currentText: this.currentRound.steps[0].answer
          });
          TweenMax.to('.step-answer', 0.5, { opacity: 1 });
        } else if (this.completed === 2) {
          if (this.isEndOfActivity) {
            this.playCongrats();
            TweenMax.delayedCall(1, () => this.completeActivity(false));
            return;
          }

          // start new round otherwise
          this.handleNewRound();
        }
      }
    }
  },
  watch: {
    expectedKey(newVal) {
      const { stepIndex } = this.progress;

      if (newVal) {
        const currentPhrase = this.currentRound.steps[0][stepIndex ? 'answer' : 'question'];
        const instructions = `Type the ${stepIndex ? 'answer' : 'question'} - ${currentPhrase}`;

        if (instructions === this.currentScreenReaderAlert) {
          return;
        }

        this.rerenderScreenReaderAlert(instructions);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.q-and-a,
.step-question,
.step-answer {
  opacity: 0;
}

.step-wrapper p {
  font-size: 30px;
}

.q-a-instructions {
  font-size: 18px;
  margin-top: 5px;
  margin-bottom: 3px;
  position: relative;

  button {
    left: -14px;
    position: absolute;
    top: 2px;
  }

  span {
    font-family: hwtfutura;
    font-size: 18px;
    left: 18px;
    margin: 0px 0px 0px 10px;
    position: relative;
    top: 10px;
    -webkit-font-smoothing: auto;
  }
}

.left-side {
  img {
    height: auto;
    width: 100%;
  }
}

.chrome-os,
.tablet {
  .q-and-a {
    .right-side {
      width: 80%;
    }
  }
}
</style>
